import React, { useState, useEffect } from 'react';
import moment from 'moment';
import queryString from 'query-string';
import { withRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { setPortal } from 'modules/portal';
import {
  dayColor,
  makeRowColumn,
  getHeadObj,
  cenversionTpCode,
  dataConverter,
} from 'lib/commonLib';
import { localDayGraphReport } from 'lib/api/localreport';
import { localDayReport } from 'lib/api/localreport';

import NewTableContainer from 'components/molecules/NewTable';
import NewFilterWrap from 'components/molecules/NewFilterWrap';
import IconAreaWrap from 'components/molecules/MbTable/IconAreaWrap';
import BarGraph from 'components/organisms/Chart/BarGraph';
import NewCommonFilter from 'components/organisms/NewCommonFilter';

const Statistics = ({ location }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { portal } = useSelector(({ portal }) => ({
    portal: portal,
  }));
  const isPopPage = queryString.parse(location.search).isPop === '1';

  let initParams = {
    sort: 'desc',
    order: 'STATS_DTTM',
    pltfomTpCode: 'all',
    itlTpCode: 'all',
    sumSub: 1,
    sDate: moment().add(-30, 'day').format('YYYYMMDD'),
    eDate: moment().format('YYYYMMDD'),
  };

  const convertKeys = ['adverId', 'sDate', 'eDate'];
  const newInitParams = dataConverter(
    queryString.parse(location.search),
    convertKeys
  );
  initParams = {
    ...initParams,
    ...newInitParams,
  };

  const [initialization, setInitialization] = useState(false);
  const [params, setParams] = useState(initParams);

  const graphList = [
    {
      title: t('지출금액'),
      data: { bar: ['ADVRTS_AMT'] },
      yAxisData: {
        left: { ADVRTS_AMT: true },
        right: {},
      },
      referenceLine: {
        ADVRTS_AMT: {
          type: 'average',
          label: t('평균선'),
          visibleValue: true,
        },
      },
    },
    {
      title: t('노출'),
      data: { bar: ['PAR_EPRS_CNT'] },
      yAxisData: {
        left: { PAR_EPRS_CNT: true },
        right: {},
      },
      referenceLine: {
        PAR_EPRS_CNT: {
          type: 'average',
          label: t('평균선'),
          visibleValue: true,
        },
      },
    },
    {
      title: 'CTR',
      data: { bar: ['CLICK_RATE'] },
      yAxisData: {
        left: { CLICK_RATE: true, isPercent: true },
        right: {},
      },
      customizeData: {
        CLICK_RATE: {
          tooltipFormat: '0.000',
          isPercent: true,
        },
      },
      referenceLine: {
        CLICK_RATE: {
          type: 'average',
          label: t('평균선'),
          visibleValue: true,
        },
      },
    },
    {
      title: 'IP',
      data: { bar: ['RGN_IP_CNT', 'NEW_IP_CNT'] },
      yAxisData: {
        left: {
          RGN_IP_CNT: true,
          NEW_IP_CNT: true,
          individually: true,
        },
        right: {},
      },
    },
  ];

  useEffect(() => {
    if (initialization === true) {
      setInitialization(false);
    }
  }, [initialization]);

  const customized = {
    STATS_DTTM: {
      makeBody: (rowObj, headsMap) => {
        return !!rowObj['PLTFOM_TP_CODE']
          ? cenversionTpCode(rowObj.PLTFOM_TP_CODE)
          : rowObj['STATS_DTTM'] !== 'total'
          ? dayColor(rowObj['STATS_DTTM'])
          : rowObj['STATS_DTTM'];
      },
    },
    ADVER_CNT: {
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'ADVER_CNT');
        return (
          rowObj['STATS_DTTM'] !== 'total' &&
          !rowObj['isChild'] &&
          makeRowColumn(rowObj['ADVER_CNT'], bodyItem1.type, bodyItem1.format)
        );
      },
      merged: !!params.adverId,
    },
    UM_PAR_EPRS_CNT: {
      makeHead: (headsMap) => {
        return t('성향');
      },
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'UM_PAR_EPRS_CNT');
        const bodyItem2 = getHeadObj(headsMap, 'UM_PAR_EPRS_RATE');
        return (
          <>
            {makeRowColumn(
              rowObj['UM_PAR_EPRS_CNT'],
              bodyItem1.type,
              bodyItem1.format
            )}
            {!!rowObj['UM_PAR_EPRS_RATE'] && !rowObj['isChild'] && (
              <>
                (
                {makeRowColumn(
                  rowObj['UM_PAR_EPRS_RATE'],
                  bodyItem2.type,
                  bodyItem2.format
                )}
                )
              </>
            )}
          </>
        );
      },
    },
    UM_PAR_EPRS_RATE: {
      merged: true,
    },
    AD_PAR_EPRS_CNT: {
      makeHead: (headsMap) => {
        return t('베이스광고');
      },
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'AD_PAR_EPRS_CNT');
        const bodyItem2 = getHeadObj(headsMap, 'AD_PAR_EPRS_RATE');
        return (
          <>
            {makeRowColumn(
              rowObj['AD_PAR_EPRS_CNT'],
              bodyItem1.type,
              bodyItem1.format
            )}
            {!!rowObj['AD_PAR_EPRS_RATE'] && !rowObj['isChild'] && (
              <>
                (
                {makeRowColumn(
                  rowObj['AD_PAR_EPRS_RATE'],
                  bodyItem2.type,
                  bodyItem2.format
                )}
                )
              </>
            )}
          </>
        );
      },
    },
    AD_PAR_EPRS_RATE: {
      merged: true,
    },
    CLICK_CNT: {
      makeBody: (rowObj, headsMap) => {
        const moveToLink =
          '/report/local/adver?isPop=1&sDate=' +
          rowObj['STATS_DTTM'] +
          '&eDate=' +
          rowObj['STATS_DTTM'] +
          '&uri=/report/local/adver';
        const itemObj = getHeadObj(headsMap, 'CLICK_CNT');
        return !isPopPage &&
          rowObj['STATS_DTTM'] !== 'total' &&
          !rowObj['isChild'] ? (
          <IconAreaWrap
            text={makeRowColumn(
              rowObj['CLICK_CNT'],
              itemObj.type,
              itemObj.format
            )}
            href={moveToLink}
            icon={'faExternalLink'}
            toolTipTitle={t('해당 날짜의 광고주통계별')}
            onClick={(e) => handlePortalUrl(moveToLink)}
          />
        ) : (
          makeRowColumn(rowObj['CLICK_CNT'], itemObj.type, itemObj.format)
        );
      },
      bodyStyle: {
        textAlign: 'center',
      },
    },
    MEDIA_SCRIPT_CNT: {
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'MEDIA_SCRIPT_CNT');
        return (
          rowObj['STATS_DTTM'] !== 'total' &&
          !rowObj['isChild'] &&
          makeRowColumn(
            rowObj['MEDIA_SCRIPT_CNT'],
            bodyItem1.type,
            bodyItem1.format
          )
        );
      },
    },
  };

  /**
   * 필터 초기화
   */
  const handleOnReset = () => {
    if (JSON.stringify(params) !== JSON.stringify(initParams)) {
      setInitialization(true);
      setParams(initParams);
      if (isPopPage && window.location.search !== '?isPop=1') {
        window.location.replace(window.location.pathname + '?isPop=1');
      }
    }
  };

  /**
   * 페이지 파라미터를 관리하는 params을 수정하는 함수
   * @param newParams
   */
  const getParam = (newParams) => {
    if (JSON.stringify(params) !== JSON.stringify(newParams)) {
      setParams(newParams);
    }
  };

  const handlePortalUrl = (url) => {
    let newPortal = portal.portal;
    let popIndex = newPortal.length;
    newPortal[popIndex] = { url: url, cnt: popIndex };
    dispatch(setPortal(newPortal));
  };

  return (
    <>
      <NewFilterWrap handleOnReset={handleOnReset}>
        <NewCommonFilter
          initialization={initialization}
          getParam={getParam}
          params={params}
          useExternalFilter={false}
        />
      </NewFilterWrap>
      <BarGraph
        params={params}
        dataReqPromise={localDayGraphReport}
        graphList={graphList}
        title={t('일자별통계 데이터 그래프')}
        labelType="MMDD"
      />
      <NewTableContainer
        params={params}
        getParam={getParam}
        dataReqPromise={localDayReport}
        customized={customized}
        expandTable
        useSearch={false}
        useFetch={false}
      />
    </>
  );
};

export default withRouter(Statistics);
